import React, { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const AdminUsersPage = ({ onShowToast }) => {
    const [roleOptions, setRoleOptions] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [savingPasswordId, setSavingPasswordId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        username: '',
        fio: '',
        password: '',
        role: 'operator',
    });

    const loadRoles = async () => {
        try {
            const r = await fetch(`${API_BASE_URL}/api/roles`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (r.ok) {
                const data = await r.json();
                const opts = (data.items || []).filter((x) => x.name !== 'tg_user');
                setRoleOptions(opts);
                setForm((prev) => {
                    if (!opts.length) return prev;
                    if (opts.some((o) => o.name === prev.role)) return prev;
                    return { ...prev, role: opts[0].name };
                });
            }
        } catch (_) {}
    };

    const load = async () => {
        try {
            setLoading(true);
            await loadRoles();
            const r = await fetch(`${API_BASE_URL}/api/users/admin-users`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!r.ok) throw new Error('Не удалось загрузить пользователей');
            const data = await r.json();
            setItems((data.items || []).map((u) => ({ ...u, roleDraft: u.role, passwordDraft: '' })));
        } catch (e) {
            if (onShowToast) onShowToast(e.message || 'Ошибка');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const createAdminUser = async () => {
        const username = form.username.trim();
        const fio = form.fio.trim();
        const password = form.password;
        const role = form.role;
        if (!username || !fio || !password) {
            if (onShowToast) onShowToast('Заполните логин, ФИО и пароль');
            return;
        }
        if (password.length < 6) {
            if (onShowToast) onShowToast('Пароль должен быть не менее 6 символов');
            return;
        }
        try {
            setCreating(true);
            const r = await fetch(`${API_BASE_URL}/api/users/admin-users`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, fio, password, role }),
            });
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err.detail || 'Ошибка создания пользователя');
            }
            setForm({ username: '', fio: '', password: '', role: 'operator' });
            if (onShowToast) onShowToast('Пользователь админки создан');
            await load();
        } catch (e) {
            if (onShowToast) onShowToast(e.message || 'Ошибка');
        } finally {
            setCreating(false);
        }
    };

    const saveRole = async (userId, role) => {
        try {
            setSavingId(userId);
            const r = await fetch(`${API_BASE_URL}/api/users/admin-users/${userId}/role`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role }),
            });
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err.detail || 'Ошибка смены роли');
            }
            if (onShowToast) onShowToast('Роль обновлена');
            await load();
        } catch (e) {
            if (onShowToast) onShowToast(e.message || 'Ошибка');
        } finally {
            setSavingId(null);
        }
    };

    const savePassword = async (userId, password) => {
        if (!password || password.length < 6) {
            if (onShowToast) onShowToast('Пароль должен быть не менее 6 символов');
            return;
        }
        try {
            setSavingPasswordId(userId);
            const r = await fetch(`${API_BASE_URL}/api/users/admin-users/${userId}/password`, {
                method: 'PATCH',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ password }),
            });
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err.detail || 'Ошибка смены пароля');
            }
            setItems((prev) => prev.map((x) => x.id === userId ? { ...x, passwordDraft: '' } : x));
            if (onShowToast) onShowToast('Пароль обновлён');
        } catch (e) {
            if (onShowToast) onShowToast(e.message || 'Ошибка');
        } finally {
            setSavingPasswordId(null);
        }
    };

    const deleteAdminUser = async (userId, username) => {
        const ok = window.confirm(`Удалить пользователя "${username}"?`);
        if (!ok) return;
        try {
            setDeletingId(userId);
            const r = await fetch(`${API_BASE_URL}/api/users/admin-users/${userId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err.detail || 'Ошибка удаления пользователя');
            }
            if (onShowToast) onShowToast('Пользователь удалён');
            await load();
        } catch (e) {
            if (onShowToast) onShowToast(e.message || 'Ошибка');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-800">Пользователи админки</h2>
            <div className="bg-white rounded-xl border border-slate-100 p-4 space-y-3">
                <h3 className="font-semibold text-slate-800">Создать пользователя админки</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                        value={form.username}
                        onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                        placeholder="Логин"
                        className="border border-slate-200 rounded px-3 py-2"
                    />
                    <input
                        value={form.fio}
                        onChange={(e) => setForm((prev) => ({ ...prev, fio: e.target.value }))}
                        placeholder="ФИО"
                        className="border border-slate-200 rounded px-3 py-2"
                    />
                    <input
                        value={form.password}
                        onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                        placeholder="Пароль"
                        type="password"
                        className="border border-slate-200 rounded px-3 py-2"
                    />
                    <select
                        value={form.role}
                        onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
                        className="border border-slate-200 rounded px-3 py-2"
                    >
                        {(roleOptions.length ? roleOptions : [{ name: 'operator', display_name: 'operator' }]).map((r) => (
                            <option key={r.name} value={r.name}>{r.display_name || r.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <button
                        onClick={createAdminUser}
                        disabled={creating}
                        className="px-4 py-2 rounded bg-emerald-600 text-white disabled:opacity-50"
                    >
                        {creating ? 'Создание...' : 'Создать'}
                    </button>
                </div>
            </div>
            {loading ? (
                <div className="text-slate-500">Загрузка...</div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-100 overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 border-b">
                            <tr>
                                <th className="text-left px-4 py-3">ID</th>
                                <th className="text-left px-4 py-3">Логин</th>
                                <th className="text-left px-4 py-3">ФИО</th>
                                <th className="text-left px-4 py-3">Роль</th>
                                <th className="text-left px-4 py-3">Пароль</th>
                                <th className="text-left px-4 py-3">Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((u) => (
                                <tr key={u.id} className="border-b border-slate-100">
                                    <td className="px-4 py-2">{u.id}</td>
                                    <td className="px-4 py-2">{u.username}</td>
                                    <td className="px-4 py-2">{u.fio}</td>
                                    <td className="px-4 py-2">
                                        <select
                                            value={u.roleDraft}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setItems((prev) => prev.map((x) => x.id === u.id ? { ...x, roleDraft: v } : x));
                                            }}
                                            className="border border-slate-200 rounded px-2 py-1"
                                        >
                                            {(roleOptions.length ? roleOptions : [{ name: 'operator', display_name: 'operator' }]).map((r) => (
                            <option key={r.name} value={r.name}>{r.display_name || r.name}</option>
                        ))}
                                        </select>
                                    </td>
                                    <td className="px-4 py-2">
                                        <div className="flex items-center gap-2">
                                            <input
                                                value={u.passwordDraft || ''}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setItems((prev) => prev.map((x) => x.id === u.id ? { ...x, passwordDraft: v } : x));
                                                }}
                                                type="password"
                                                placeholder="Новый пароль"
                                                className="border border-slate-200 rounded px-2 py-1"
                                            />
                                            <button
                                                onClick={() => savePassword(u.id, u.passwordDraft || '')}
                                                disabled={savingPasswordId === u.id || !(u.passwordDraft || '')}
                                                className="px-3 py-1.5 rounded bg-slate-700 text-white disabled:opacity-50"
                                            >
                                                {savingPasswordId === u.id ? '...' : 'Сменить'}
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-4 py-2">
                                        <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => saveRole(u.id, u.roleDraft)}
                                            disabled={savingId === u.id || u.roleDraft === u.role}
                                            className="px-3 py-1.5 rounded bg-indigo-600 text-white disabled:opacity-50"
                                        >
                                            Сохранить
                                        </button>
                                            <button
                                                onClick={() => deleteAdminUser(u.id, u.username)}
                                                disabled={deletingId === u.id}
                                                title="Удалить пользователя"
                                                className="px-3 py-1.5 rounded bg-rose-600 text-white disabled:opacity-50"
                                            >
                                                {deletingId === u.id ? '...' : <Trash2 size={16} />}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {items.length === 0 && <div className="p-4 text-slate-500">Пользователей админки нет.</div>}
                </div>
            )}
        </div>
    );
};

export default AdminUsersPage;
